import { useDeviceTypes } from 'core/contexts/device-types/hooks/use-device-types.hook';
import { settingsModulePaths } from 'core/routes/routes';
import { useCallback, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { DEVICE_CONFIGURATION_TABS } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-dashboard/settings-device-configuration-dashboard.constants';
import { useCustomAlarmsDataHandler } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-data-handler/use-custom-alarms-data-handler.hook';
import { useCustomAlarmsSaver } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-saver/use-custom-alarms-saver.hook';
import { useCustomAlarmsFormContext } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-form-context/use-custom-alarms-form-context.hook';

export const useCustomAlarmsForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { getDeviceTypeDetailsBySlug } = useDeviceTypes();

    const [isVisible, setIsVisible] = useState<boolean>(true);

    // Device type should always be found on hook mount
    const deviceType = getDeviceTypeDetailsBySlug(id!)!;

    const handleBackToDashboard = useCallback(() => {
        setIsVisible(false);
        navigate(generatePath(settingsModulePaths.deviceConfiguration, { id: id ?? '' }), {
            state: { tab: DEVICE_CONFIGURATION_TABS.CUSTOM_ALARMS },
        });
    }, [id, navigate]);

    const scheduleBackToDashboard = useCallback(() => {
        globalThis.setTimeout(() => {
            handleBackToDashboard();
        }, 0);
    }, [handleBackToDashboard]);

    const {
        applyEventSelection,
        eventsOptions,
        eventsValid,
        form,
        formValid,
        windowDaysValidity,
        getNameInputError,
        canSubmit,
        isLoadingEvents,
        markSubmitAttempted,
        nameValid,
        notificationsTouched,
        selectedEvents,
        setField,
        setNotificationField,
        setResettable,
        submitAttempted,
        unsavedData,
    } = useCustomAlarmsDataHandler({
        deviceTypeId: deviceType?.id,
        onClose: handleBackToDashboard,
    });

    const { mode, isFormLoading, customAlarmDefinition } = useCustomAlarmsFormContext();

    const { isSubmitting, nameCustomValidationCode, resetNameCustomValidationCode, saveCustomAlarm, editCustomAlarm } =
        useCustomAlarmsSaver({
            deviceTypeId: deviceType?.id,
            onSuccess: () => {
                unsavedData.setIsUnsavedChanges(false);
                scheduleBackToDashboard();
            },
        });

    const handleClose = () => {
        unsavedData.showUnsavedChangesIfNeededOrClose(() => {
            scheduleBackToDashboard();
        });
    };

    const handleNameChange = (value: string) => {
        resetNameCustomValidationCode();
        setField('name', value);
    };

    const handleSubmit = () => {
        markSubmitAttempted();
        resetNameCustomValidationCode();

        if (!canSubmit) {
            return;
        }

        if (mode === 'add') {
            saveCustomAlarm(form, selectedEvents);
        } else if (customAlarmDefinition) {
            editCustomAlarm(form, selectedEvents, customAlarmDefinition.id);
        }
    };

    return {
        deviceTypeName: deviceType?.name ?? '',
        isVisible,
        isSubmitting,
        isLoadingEvents,
        submitAttempted,
        nameCustomValidationCode,
        form,
        eventsOptions,
        nameValid,
        eventsValid,
        formValid,
        canSubmit,
        windowDaysValidity,
        getNameInputError,
        setField,
        setNotificationField,
        applyEventSelection,
        setResettable,
        setName: handleNameChange,
        notificationsTouched,
        handleBackToDashboard,
        handleClose,
        handleSubmit,
        mode,
        isFormLoading,
    };
};
